import { useCallback, useEffect, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { IonAlert } from '@ionic/react';
import { useI18n } from '../i18n/I18nProvider';
import {
  checkApiCompat,
  dismissSoftPrompt,
  isSoftDismissed,
} from '../lib/apiCompat';
import { installApiFetchGate, setApiBlocked } from '../lib/apiGate';

const UPGRADE_PATH = '/upgrade';

/**
 * On app open / resume: compare baked requiredBackendVersion with live API.
 * Soft mismatch → optional update popup. Force mismatch → block API + /upgrade.
 */
const VersionCompatRunner: React.FC = () => {
  const { t } = useI18n();
  const history = useHistory();
  const location = useLocation();
  const [softOpen, setSoftOpen] = useState(false);
  const [softVersions, setSoftVersions] = useState<{
    required: string;
    live: string;
  } | null>(null);
  const checkingRef = useRef(false);

  const runCheck = useCallback(async () => {
    if (checkingRef.current) return;

    checkingRef.current = true;
    try {
      installApiFetchGate();
      const status = await checkApiCompat();

      if (status.kind === 'ok') {
        setApiBlocked(false);
        setSoftOpen(false);
        return;
      }

      if (status.kind === 'unreachable') {
        // Don't block or nag when API is briefly unavailable.
        return;
      }

      if (status.forceMatch) {
        setApiBlocked(true);
        setSoftOpen(false);
        if (location.pathname !== UPGRADE_PATH) {
          history.replace(`${UPGRADE_PATH}?force=1`);
        }
        return;
      }

      setApiBlocked(false);
      if (location.pathname === UPGRADE_PATH) return;
      if (isSoftDismissed(status.required, status.live)) return;

      setSoftVersions({ required: status.required, live: status.live });
      setSoftOpen(true);
    } finally {
      checkingRef.current = false;
    }
  }, [history, location.pathname]);

  useEffect(() => {
    void runCheck();
  }, [runCheck]);

  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === 'visible') {
        void runCheck();
      }
    };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onVisible);

    // Capacitor: visibility/focus can miss background→foreground; App plugin is reliable.
    let removeAppListener: (() => void) | undefined;
    void import('@capacitor/core')
      .then(({ Capacitor }) => {
        if (!Capacitor.isNativePlatform()) return undefined;
        return import('@capacitor/app');
      })
      .then((appMod) => {
        if (!appMod) return;
        return appMod.App.addListener('appStateChange', ({ isActive }) => {
          if (isActive) void runCheck();
        });
      })
      .then((handle) => {
        if (handle) removeAppListener = () => void handle.remove();
      })
      .catch(() => {
        // Web / plugin unavailable — visibility listeners are enough.
      });

    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onVisible);
      removeAppListener?.();
    };
  }, [runCheck]);

  return (
    <IonAlert
      isOpen={softOpen}
      header={t('versionCompat.soft.title')}
      message={t('versionCompat.soft.message', {
        required: softVersions?.required ?? '',
        live: softVersions?.live ?? '',
      })}
      backdropDismiss
      buttons={[
        {
          text: t('versionCompat.soft.later'),
          role: 'cancel',
          handler: () => {
            if (softVersions) {
              dismissSoftPrompt(softVersions.required, softVersions.live);
            }
            setSoftOpen(false);
          },
        },
        {
          text: t('versionCompat.soft.update'),
          handler: () => {
            setSoftOpen(false);
            history.push(UPGRADE_PATH);
          },
        },
      ]}
      onDidDismiss={() => setSoftOpen(false)}
    />
  );
};

export default VersionCompatRunner;
